import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, from, Observable, of, throwError } from 'rxjs';
import { finalize, map, mergeMap, switchMap } from 'rxjs/operators';
import { environment as env } from '../../environments/environment';
import { UserQuoteStatus } from '../data-types/xero-data';
import { ApiAuthHttpClient } from '../http/ApiAuthHttpClient';
import { AlertService } from '../services/alert.service';
import { CookieService } from '../services/cookie.service';
import { ToolsService } from '../services/tools.service';
import { Store } from '../store';
import {
    Basket,
    BasketAddress,
    BasketItem,
    CalculateDiscountPrice, defaultBasketValues,
    defaultSkuPrice,
    defaultSkuPriceTotals,
    DiscountValue,
    SkuPrice,
    SkuPriceTotals,
} from '../data-types/basket.data';
import { UserData } from '../data-types/user-data';

@Injectable({
    providedIn: 'root',
})
export class BasketService {
    private basketSave$: BehaviorSubject<{ type: 'shipmentUpdated', data: any }> = new BehaviorSubject(null);
    readonly onBasketSave = this.basketSave$.asObservable();

    newBasket: Basket = {
        type: 'order',
        fulfilmentMethod: 'delivery',
        noDeliveryRequired: false,
        lastStep: 'entry',
        source: 'admin',
        sampleOnly: false,
        potentialQty: 0,
        items: [],
        samples: [],
        customerType: 'existing',
        voucher: undefined,
        voucherCodeEntered: '',
        deliveryInstructions: '',
        differentDeliveryAddress: false,
        deliveryAddress: undefined,
        deliveryPostcode: '',
        deliveryAddresses: [],
        deliveryRestrictions: [],
        billingAddress: undefined,
        customer: undefined,
        guest: true,
        rewardsDiscountPercentage: 0,
        allowBoards: false,
        gravelDriveway: false,
        paymentMethod: 'BACS',
        xeroQuoteId: undefined,
        createdBy: undefined,
        discount: {
            type: 'pound',
            value: 0,
        },
        customDelivery: {
            value: 600,
            active: false,
        },
        values: { ...defaultBasketValues },
        token: undefined,
        shipments: [],
        restrictions: {
            slope: false,
            gravel: false,
            boards: false,
            offload: false
        },
        deliveryOptions: {
            allowPremium: true,
            premium: 0,
            economy: 0,
            geo: {
                travelKM: 0,
                travelMiles: 0,
                travelMinutes: 0,
                lat: undefined,
                lng: undefined,
            },
        },
        selectedService: null,
    } as Basket;

    static getItemTotals(
        itemNet,
        isSample,
        rewardDiscount = null,
        voucher: { discountType: number; discountValue: number } | null = null
    ): SkuPriceTotals {
        itemNet = +itemNet;
        const totals: SkuPriceTotals = defaultSkuPriceTotals();

        if (isSample) {
            return totals;
        }

        const itemVat = +((+itemNet / 100) * 20).toFixed(2);

        totals.preDiscount.net = +itemNet;
        totals.preDiscount.vat = +itemVat;
        totals.preDiscount.gross = +itemNet + itemVat;

        totals.net = +itemNet;
        totals.vat = +itemVat;
        totals.gross = +itemNet + itemVat;

        if (rewardDiscount) {
            // basket.customer.rewardDiscount
            totals.rewardDiscount = +(totals.preDiscount.net * (+rewardDiscount / 100)).toFixed(2);
            totals.rewardDiscount = +((+totals.preDiscount.net / 100) * +rewardDiscount).toFixed(2);
            totals.voucherDiscount = 0;
            // item.rewardDiscount = +((+item.totalPricePreDiscount / 100) * +rewardsDiscountPercentage).toFixed(2);
        }

        if (voucher) {
            if (voucher.discountType === 1) {
                totals.voucherDiscount = (+totals.preDiscount.net / 100) * +voucher.discountValue;
            }
        }

        totals.net = +totals.preDiscount.net - +totals.rewardDiscount - +totals.voucherDiscount;
        totals.vat = +((+totals.preDiscount.net / 100) * 20).toFixed(2);
        totals.gross = +totals.net + +totals.vat;

        return totals;
    }

    constructor(
        private router: Router,
        private http: ApiAuthHttpClient,
        private store: Store,
        private cookieService: CookieService,
        private toolsService: ToolsService,
        private alertService: AlertService
    ) {
    }

    public basketCustomerCanReceiveEmail(basket: Pick<Basket, 'customer'> | any): boolean {
        const customer = basket?.customer;

        if (!customer) {
            return false;
        }

        if (customer.noEmail === true || `${customer.noEmail ?? ''}` === '1') {
            return false;
        }

        const email = `${customer.contact?.email || customer.email || customer.username || ''}`.trim().toLowerCase();

        if (!email || !email.includes('@')) {
            return false;
        }

        return !email.endsWith('@no-email.yorkstonesupplies.local');
    }

    private hasUsablePricingTotals(item: BasketItem): boolean {
        const totals = item && item.pricing && item.pricing.totals;
        return !!totals && totals.net != null && totals.vat != null && totals.gross != null;
    }

    private firstPresent(...values: any[]): any {
        return values.find((value) => value !== undefined && value !== null && value !== '');
    }

    private clone<T>(value: T): T {
        return value ? JSON.parse(JSON.stringify(value)) : value;
    }

    private addPricingFinish(finishes: any, key: string, option: any): void {
        if (!finishes[key] && option && option.skuId) {
            finishes[key] = { skuId: option.skuId };
        }
    }

    private buildPricingDimensions(item: BasketItem, payload: any): any {
        const basketItem: any = item;
        const dimensions = payload.dimensions ? this.clone(payload.dimensions) : {};

        if (Object.keys(dimensions).length || !basketItem.width || !basketItem.length || !basketItem.height) {
            return dimensions;
        }

        dimensions.l = {
            trueSize: basketItem.length.size,
            size: +basketItem.length.size / 1000,
            skuId: basketItem.length.skuId || '',
        };
        dimensions.w = {
            trueSize: basketItem.width.size,
            size: +basketItem.width.size / 1000,
            skuId: basketItem.width.skuId || '',
        };
        dimensions.t = {
            trueSize: basketItem.height.size,
            size: +basketItem.height.size / 1000,
            skuId: basketItem.height.skuId || '',
        };

        return dimensions;
    }

    private buildPricingPayloadForBasketItem(item: BasketItem, basket: Basket): any | null {
        if (!item || item.sample || item.product?.productCode === 'bespoke' || item.product?.isBespoke) {
            return null;
        }

        const basketItem: any = item;
        const pricing: any = item.pricing;
        const existingPayload = pricing?.payload ? this.clone(pricing.payload) : {};
        const product: any = item.product || {};
        const productDetailId = +this.firstPresent(existingPayload.productDetailId, item.productDetailId, basketItem.id);
        const quantity = +this.firstPresent(item.qty, existingPayload.quantity);

        if (!Number.isFinite(productDetailId) || productDetailId <= 0 || !Number.isFinite(quantity) || quantity <= 0) {
            return null;
        }

        const finishes = existingPayload.finishes ? this.clone(existingPayload.finishes) : {};
        this.addPricingFinish(finishes, 'top', (item as any).finishTop);
        this.addPricingFinish(finishes, 'finishAdd', (item as any).finishAdd);
        this.addPricingFinish(finishes, 'masonry', (item as any).masonry);
        this.addPricingFinish(finishes, 'masonry2', (item as any).masonry2);
        this.addPricingFinish(finishes, 'bottom', (item as any).finishBottom);
        this.addPricingFinish(finishes, 'weathering', (item as any).weathering);

        return {
            ...existingPayload,
            productDetailId,
            productId: this.firstPresent(existingPayload.productId, product.id),
            skuId: this.firstPresent(existingPayload.skuId, item.skuId),
            applyDiscount: this.firstPresent(existingPayload.applyDiscount, true),
            quantity,
            dimensions: this.buildPricingDimensions(item, existingPayload),
            supplierId: this.firstPresent(existingPayload.supplierId, product.supplier?.id, product.supplier, ''),
            rangeId: this.firstPresent(existingPayload.rangeId, product.rangeId, product.range?.id),
            productType: this.firstPresent(existingPayload.productType, product.typeId, product.type?.id),
            finishId: this.firstPresent(existingPayload.finishId, product.finishId?.id, product.finishId, product.finish?.id, product.finish),
            pricingMethod: this.firstPresent(existingPayload.pricingMethod, item.pricingMethod, product.pricingMethod, ''),
            sellUnit: this.firstPresent(existingPayload.sellUnit, item.sellUnit?.id, item.sellUnit, product.sellUnit?.id, product.sellUnit),
            margin: this.firstPresent(existingPayload.margin, 0),
            discount: this.firstPresent(existingPayload.discount, item.unitPriceDiscountPerc, 0),
            finishes,
            customerId: this.firstPresent(existingPayload.customerId, existingPayload.userId, basket.customer?.id),
            userId: this.firstPresent(existingPayload.userId, existingPayload.customerId, basket.customer?.id),
            isBespoke: this.firstPresent(existingPayload.isBespoke, false),
        };
    }

    private applyPricingResultToBasketItem(item: BasketItem, result: any, payload: any): void {
        if (!result || !result.pricing || result.pricing.priceUnavailable === true || result.pricing.net == null) {
            return;
        }

        const pricing: SkuPrice = {
            ...result.pricing,
            payload,
            quantity: result.quantity || { requested: { quantity: item.qty } },
        } as any;
        const totals = pricing.totals;
        const qty = +payload.quantity || +item.qty || 1;

        if (!totals || totals.net == null || totals.vat == null || totals.gross == null) {
            return;
        }

        if (!totals.preDiscount) {
            totals.preDiscount = {
                net: totals.net,
                vat: totals.vat,
                gross: totals.gross,
            };
        }
        totals.qty = qty;

        item.pricing = pricing;
        item.qty = qty;
        item.unitPrice = +(totals.net / qty).toFixed(2);
        item.unitPriceGross = +(totals.preDiscount.net / qty).toFixed(2);
        item.unitPriceDiscount = +(item.unitPriceGross - item.unitPrice).toFixed(2);
        item.totalPrice = +totals.net;
        const basketItem: any = item;
        basketItem.totalVat = +totals.vat;
        basketItem.totalPriceGross = +totals.gross;
        item.totalDiscount = +(totals.discount || 0);
        item.totalPricePreDiscount = +totals.preDiscount.net;
        basketItem.totals = totals;
    }

    private refreshBasketItemPricing(basket: Basket): Promise<void> {
        const payloads: Array<{ index: number; payload: any }> = [];

        (basket.items || []).forEach((item, index) => {
            const payload = this.buildPricingPayloadForBasketItem(item, basket);
            if (payload) {
                payloads.push({ index, payload });
            }
        });

        if (!payloads.length) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            this.http.post(env.apiPath + 'pricing/sku', {
                itemsToPrice: payloads.map(({ payload }) => payload),
            }).subscribe(({ data }: any) => {
                const results = Array.isArray(data) ? data : [];
                results.forEach((result, resultIndex) => {
                    const mapped = payloads[resultIndex];
                    if (mapped && basket.items[mapped.index]) {
                        this.applyPricingResultToBasketItem(basket.items[mapped.index], result, mapped.payload);
                    }
                });
                resolve();
            }, () => resolve());
        });
    }

    public normaliseBasketState<T extends Basket>(basket: T): T {
        if (!basket) {
            return basket;
        }

        if (!basket.items) {
            basket.items = [];
        }
        if (!basket.samples) {
            basket.samples = [];
        }
        if (!basket.deliveryAddresses) {
            basket.deliveryAddresses = [];
        }
        if (!basket.values) {
            basket.values = { ...defaultBasketValues };
        }
        if (!basket.discount) {
            basket.discount = {
                type: 'pound',
                value: 0,
            };
        }
        if (!basket.customDelivery) {
            basket.customDelivery = {
                value: 600,
                active: false,
            };
        }
        if (!basket.fulfilmentMethod) {
            basket.fulfilmentMethod = basket.noDeliveryRequired ? 'collection' : 'delivery';
        }

        const customerAddress = basket.customer?.address;
        const firstDeliveryAddress = basket.deliveryAddresses.length ? basket.deliveryAddresses[0] : undefined;
        const requiresDelivery = this.basketRequiresDelivery(basket);
        const selectedDeliveryAddress = requiresDelivery
            ? basket.deliveryAddress || firstDeliveryAddress || (!basket.differentDeliveryAddress ? customerAddress : undefined)
            : undefined;

        if (!basket.billingAddress && customerAddress) {
            basket.billingAddress = customerAddress;
        }

        if (!requiresDelivery) {
            this.markBasketForCollection(basket);
            return basket;
        }

        if (selectedDeliveryAddress) {
            basket.deliveryAddress = selectedDeliveryAddress;
            basket.deliveryPostcode = basket.deliveryAddress.postcode || basket.deliveryPostcode || '';

            const alreadyListed = basket.deliveryAddresses.some((address) => this.isSameBasketAddress(address, basket.deliveryAddress));

            if (!alreadyListed) {
                basket.deliveryAddresses = [basket.deliveryAddress, ...basket.deliveryAddresses];
            }
        } else if (!basket.deliveryAddress) {
            basket.deliveryPostcode = basket.deliveryPostcode || '';
        }

        return basket;
    }

    private isSameBasketAddress(addressA: BasketAddress, addressB: BasketAddress): boolean {
        if (!addressA || !addressB) {
            return false;
        }

        if (addressA.id && addressB.id) {
            return addressA.id === addressB.id;
        }

        const addressFields: Array<keyof BasketAddress> = [
            'add1',
            'add2',
            'add3',
            'town',
            'county',
            'country',
            'postcode',
        ];

        return addressFields.every((field) =>
            `${addressA[field] || ''}`.trim().toLowerCase() === `${addressB[field] || ''}`.trim().toLowerCase()
        );
    }

    public basketRequiresDelivery(basket: Basket): boolean {
        return !!basket && basket.noDeliveryRequired !== true && basket.fulfilmentMethod !== 'collection';
    }

    public markBasketForCollection(basket: Basket): Basket {
        basket.noDeliveryRequired = true;
        basket.fulfilmentMethod = 'collection';
        basket.deliveryAddress = undefined;
        basket.deliveryPostcode = '';
        basket.shipments = [];
        basket.selectedService = null;

        if (basket.customDelivery) {
            basket.customDelivery.active = false;
        }

        if (basket.values) {
            basket.values.delivery = 0;
            basket.values.deliveryVat = 0;
        }

        return basket;
    }

    public markBasketForDelivery(basket: Basket): Basket {
        basket.noDeliveryRequired = false;
        basket.fulfilmentMethod = 'delivery';
        return this.normaliseBasketState(basket);
    }

    private ensurePricingTotals(item: BasketItem): BasketItem {
        if (!item || !item.pricing || this.hasUsablePricingTotals(item)) {
            return item;
        }

        item.pricing.totals = this.calculateLineTotals(+item.totalPrice, !!item.sample);
        return item;
    }

    private getOrderTotalsErrorMessage(error: any): string {
        const message = error && error.error && error.error.message ? error.error.message : error && error.message;

        if (Array.isArray(message)) {
            return message.join(', ');
        }

        return message || 'One or more basket items are no longer available at the configured price. Please remove or edit them before checkout.';
    }

    private isPricingValidationError(error: any): boolean {
        const message = this.getOrderTotalsErrorMessage(error).toLowerCase();

        return (
            message.includes('one or more item prices have changed') ||
            message.includes('has unavailable pricing') ||
            message.includes('no longer available at the configured price')
        );
    }

    private handleOrderTotalsError(basket: Basket, error: any): void {
        basket.hasTotalsError = true;
        basket.lastTotalsErrorMessage = this.getOrderTotalsErrorMessage(error);

        if (this.isPricingValidationError(error)) {
            this.markBasketPricingUnavailable(basket, error);
            return;
        }

        this.store.set('basket', basket);
        this.alertService.error([basket.lastTotalsErrorMessage]);
    }

    private markBasketPricingUnavailable(basket: Basket, error: any): void {
        basket.hasUnavailableItem = true;
        basket.hasTotalsError = true;
        basket.lastTotalsErrorMessage = this.getOrderTotalsErrorMessage(error);
        this.store.set('basket', basket);
        this.alertService.error([basket.lastTotalsErrorMessage]);
        this.saveBasketToStorage(basket).subscribe({
            error: () => undefined,
        });
    }

    public basketHasUnavailablePricing(basket: Basket): boolean {
        return (
            !!basket &&
            (
                basket.hasUnavailableItem ||
                (basket.items || []).some((item) => item.pricing?.priceUnavailable === true || item.pricing?.net == null)
            )
        );
    }

    public blockUnavailablePricing(basket: Basket): boolean {
        if (!this.basketHasUnavailablePricing(basket)) {
            return false;
        }

        this.alertService.simple('One or more basket items are no longer available at the configured price. Please remove or edit them before checkout.');
        return true;
    }

    private shouldRecalculateDeliveryAfterBasketMutation(basket: Basket): boolean {
        return this.basketRequiresDelivery(basket) &&
            !basket.customDelivery?.active &&
            !!basket.deliveryAddress &&
            !!((basket.items || []).length || (basket.samples || []).length);
    }

    getBasketId() {
        let basketId = localStorage.getItem('yssAdminBasketId');

        if (!basketId) {
            basketId = this.toolsService.newUUID();
            this.setBasketId(basketId);
        }
        return basketId;
    }

    setBasketId(basketId) {
        localStorage.setItem('yssAdminBasketId', basketId);
    }

    createNewBasket() {
        const user = this.store.selectForLocal('user');
        const newBasket = JSON.parse(JSON.stringify(this.newBasket));
        newBasket.createdBy = user;
        newBasket.id = this.getBasketId();
        this.setBasketId(newBasket.id);

        this.saveBasketToStorage(newBasket);
        return newBasket;
    }

    getCurrentBasket() {
        const basketId = this.getBasketId();
        return this.http.get(env.apiPath + 'basket/' + basketId).pipe(
            mergeMap(({ data }: any) => {
                let basketData;
                if (data) {
                    if (!data.samples) {
                        data.samples = [];
                    }
                    basketData = this.normaliseBasketState(data);
                    this.store.set('basket', basketData);
                    basketData.items.forEach((item) => this.ensurePricingTotals(item));
                    return this.calculateBasketTotal(
                        basketData,
                        this.shouldRecalculateDeliveryAfterBasketMutation(basketData)
                    );
                }

                return of(this.createNewBasket());
            })
        );
    }

    getBasket(basketId) {
        return this.http.get<Basket>(env.apiPath + 'basket/' + basketId).pipe(
            mergeMap(({ data }: any) => {
                let basketData: Basket;
                if (data) {
                    basketData = this.normaliseBasketState(data as Basket);

                    basketData.items.forEach((item) => this.ensurePricingTotals(item));
                    this.store.set('basket', basketData);
                    return this.calculateBasketTotal(basketData);
                }

                return of<Basket>(this.store.selectForLocal<Basket>('basket'));
            })
        );
    }

    replaceBasketItem(item, idx): Promise<Basket> {
        const basket: Basket = this.store.selectForLocal<Basket>('basket');

        item.totalDiscount = 0;
        item.totalPricePreDiscount = +item.totalPrice;
        item.unitPriceDiscount = 0;
        item.unitPriceDiscountPerc = 0;
        item.unitPriceGross = +item.unitPrice + +item.unitVatPrice;

        basket.items[idx] = item;

        return this.calculateBasketTotal(basket, this.shouldRecalculateDeliveryAfterBasketMutation(basket));
    }

    addToBasket(item: BasketItem): Promise<Basket> {
        let basket: Basket = this.store.selectForLocal<Basket>('basket');
        if (!basket.samples) {
            basket.samples = [];
        }
        // check for duplicate products and add qty if found (non bespoke only);
        let dupeFound = false;

        if (item.product.productCode !== 'bespoke') {
            dupeFound = this.dupeCheck(item, basket);
        } else {
            /*item.totalDiscount = 0;
            item.totalPricePreDiscount = +item.totalPrice;
            item.unitPriceDiscount = 0;
            item.unitPriceDiscountPerc = 0;
            item.unitPriceGross = +item.unitPrice + +item.unitVatPrice;*/
        }

        if (item.product.range) {
            delete item.product.range.tileText;
            delete item.product.range.bannerText;
            delete item.product.range.description;
        }

        const lastBasketItemAdded = {
            isDupe: dupeFound,
            isSample: item.sample,
            sampleCount: basket.samples.length,
            item,
        };
        this.store.set('lastBasketItemAdded', lastBasketItemAdded);

        if (item.sample) {
            item.qty = 1;
            if (!dupeFound && basket.samples.length < 3) {
                basket.samples.push(item);
            }
        }
        if (!item.sample) {
            if (!dupeFound) {
                basket.items.push(item);
            }
        }

        basket = this.checkForSampleOnly(basket);

        return this.calculateBasketTotal(basket, this.shouldRecalculateDeliveryAfterBasketMutation(basket));
    }

    dupeCheck(item: BasketItem, basket: Basket): boolean {
        let dupeFound = false;

        if (!item.sample) {
            for (let i = 0; i < basket.items.length; i++) {
                if (
                    basket.items[i].product.productCode + '-' + basket.items[i].skuId ===
                    item.product.productCode + '-' + item.skuId &&
                    !basket.items[i].sample
                ) {

                    if (basket.items[i].product.jobLot) {
                        if (basket.items[i].product.allowBreakage) {
                            basket.items[i].qty = +basket.items[i].qty + +item.qty;
                            if (basket.items[i].qty > +basket.items[i].stockCheckResult.stockFree) {
                                basket.items[i].qty = +basket.items[i].stockCheckResult.stockFree;
                            }
                        } else {
                            basket.items[i].qty = +basket.items[i].stockCheckResult.stockFree;
                        }
                    } else {
                        basket.items[i].qty = +basket.items[i].qty + +item.qty;
                    }


                    basket.items[i].piecesRequired = Math.ceil(+basket.items[i].qty / +basket.items[i].singlePieceSize);
                    basket.items[i].totalPrice = +(+basket.items[i].qty * +basket.items[i].unitPrice).toFixed(2);

                    basket.items[i].unitPriceDiscount = +(
                        +basket.items[i].unitPriceGross - +basket.items[i].unitPrice
                    ).toFixed(2);
                    basket.items[i].totalDiscount = +basket.items[i].unitPriceDiscount * +basket.items[i].qty;
                    basket.items[i].totalPricePreDiscount = +basket.items[i].unitPriceGross * +basket.items[i].qty;

                    basket.items[i].pricing.totals = this.calculateLineTotals(basket.items[i].totalPrice, false);

                    dupeFound = true;
                }
            }
        } else {
            for (let i = 0; i < basket.samples.length; i++) {
                if (
                    basket.samples[i].sample &&
                    basket.samples[i].product.finishId === item.product.finishId &&
                    basket.samples[i].product.rangeId === item.product.rangeId
                ) {
                    dupeFound = true;
                }
            }
        }

        return dupeFound;
    }

    delete(basketId) {
        return this.http.delete(env.apiPath + 'basket/' + basketId).pipe(map((data: any) => data));
    }

    search() {
        // @todo fix this typing
        const baskets = this.store.selectForLocal('baskets');
        const dataToSend = {
            params: baskets.params,
        };
        return this.http.post(env.apiPath + 'basket/search', dataToSend).pipe(
            map((data: any) => {
                baskets.data = data.data.data;
                baskets.params.pages = Math.ceil(data.data.totalRecords / baskets.params.limit);
                baskets.params.totalRecords = data.data.totalRecords;
                baskets.params.pageArray = [];
                for (let i = 0; i < baskets.params.pages; i++) {
                    baskets.params.pageArray.push(i);
                }

                this.store.set('baskets', baskets);
                return data;
            })
        );
    }

    toggleDeliveryAddress() {
        const basket: Basket = this.store.selectForLocal<Basket>('basket');
        if (!basket.differentDeliveryAddress) {
            basket.differentDeliveryAddress = true;
        } else {
            basket.differentDeliveryAddress = false;
        }
        this.saveBasketChanges(basket, 'internal');
    }

    saveBasketChanges(basket: Basket, src) {
        this.store.set('basket', basket);
        this.saveBasketToStorage(basket).subscribe();
    }

    setGuest(v) {
        const basket: Basket = this.store.selectForLocal<Basket>('basket');
        basket.guest = v;
        this.saveBasketChanges(basket, 'internal');
    }

    setToken(v) {
        const basket: Basket = this.store.selectForLocal<Basket>('basket');
        basket.token = v;
        this.saveBasketChanges(basket, 'internal');
    }

    calculateLineTotals(itemNet, isSample): SkuPriceTotals {
        const basket: Basket = this.store.selectForLocal<Basket>('basket');

        return BasketService.getItemTotals(itemNet, isSample, basket.customer?.rewardDiscount ?? null);
    }

    buildBespokeLinePricing(unitNet, lineNet, qty, isSample = false): SkuPrice {
        const pricing = defaultSkuPrice();
        const normalisedUnitNet = Number.isFinite(+unitNet) ? +(+unitNet).toFixed(2) : 0;
        const normalisedLineNet = Number.isFinite(+lineNet) ? +(+lineNet).toFixed(2) : 0;
        const normalisedQty = Number.isFinite(+qty) ? +qty : 0;
        const unitVat = +((normalisedUnitNet / 100) * 20).toFixed(2);

        pricing.basePrice = normalisedUnitNet;
        pricing.net = normalisedUnitNet;
        pricing.vat = unitVat;
        pricing.gross = +(normalisedUnitNet + unitVat).toFixed(2);
        pricing.preDiscount = {
            net: normalisedUnitNet,
            vat: unitVat,
            gross: +(normalisedUnitNet + unitVat).toFixed(2),
        };
        pricing.discount = {
            preDiscountValue: normalisedUnitNet,
            reward: {
                percentage: 0,
                value: 0,
            },
            product: {
                percentage: 0,
                value: 0,
            },
        };
        pricing.discountToUse = 'none';
        pricing.totals = this.calculateLineTotals(normalisedLineNet, isSample);
        pricing.totals.qty = normalisedQty;

        return pricing;
    }

    async calculateBasketTotal(basket: Basket, andDelivery = false): Promise<Basket> {
        basket = this.normaliseBasketState(basket);
        await this.refreshBasketItemPricing(basket);

        if (!this.basketRequiresDelivery(basket)) {
            basket = this.markBasketForCollection(basket);
            andDelivery = false;
        }

        basket.values = {
            ...basket.values,
            ...{
                gross: 0,
                vat: 0,
                net: 0,
                productDiscount: 0,
                voucherDiscount: 0,
                rewardDiscount: 0,
                adminDiscount: 0,
            },
        };

        if (!basket.items.length && !basket.samples.length) {
            basket.values.delivery = 0;
            basket.values.deliveryVat = 0;
            basket.shipments = [];
        }
        if (basket.sampleOnly) {
            basket.values.delivery = 0;
            basket.values.deliveryVat = 0;
        }
        if (this.basketRequiresDelivery(basket) && !basket.deliveryAddress && !basket.customDelivery.active) {
            basket.values.delivery = 0;
            basket.values.deliveryVat = 0;
            basket.shipments = [];
        }

        /* DISCOUNTS logic 12/04/2022
         * If a discount code is used in conjunction with a current discount already on a customers account,
         * always allocate the highest discount between the automatic and manually added discount code. So example being,
         * if a customer has a 10% discount on their account all year round, and try to use a discount code that is 15%
         * off then the customer will only get 15% off - NOT 10% plus 15%. If the discount code is monetary (e.g. £50 off),
         * they will get whichever is the highest discount (£50 off or 10%), NOT both.
         * */

        return new Promise<Basket>((res) => {
            let startObs = null;
            if (andDelivery) {
                startObs = this.calculateDeliveryCosts(basket)
                    .pipe(
                        switchMap((data) => {
                            const previousShipments = basket.shipments;
                            basket.shipments = [];

                            if (!data.errors.length) {
                                basket.qualifiedForFreeDelivery = data.qualifiedForFreeDelivery;
                                for (const shipment of data.shipments) {
                                    basket.shipments.push(shipment);
                                }
                            }
                            basket.deliveryOptions.premium = 0;
                            basket.deliveryOptions.economy = 0;
                            basket.deliveryOptions.allowPremium = true;

                            // this.saveBasketChanges(basket, 'CalculateDeliveryCosts');
                            this.recalculateBasketDelivery(basket);

                            return this.reCalculateOrder({
                                items: basket.items,
                                delivery: { amount: basket.values.delivery, vat: basket.values.deliveryVat },
                                voucher: basket.voucher ?? null,
                                rewardsDiscountPercentage: basket.customer ? basket.customer.rewardDiscount ?? 0 : 0,
                                discount: basket.discount
                                    ? { discountValue: basket.discount.value.toString(), discountType: basket.discount.type }
                                    : null,
                            }).pipe(finalize(() => {
                                this.basketSave$.next({
                                    type: 'shipmentUpdated',
                                    data: previousShipments
                                });
                            }));
                        }),
                    );
            } else {
                startObs = this.reCalculateOrder({
                    items: basket.items,
                    delivery: { amount: basket.values.delivery, vat: basket.values.deliveryVat },
                    voucher: basket.voucher ?? null,
                    rewardsDiscountPercentage: basket.customer ? basket.customer.rewardDiscount ?? 0 : 0,
                    discount: basket.discount
                        ? { discountValue: basket.discount.value.toString(), discountType: basket.discount.type }
                        : null,
                });
            }

            startObs.subscribe(({ data }: any) => {
                if (data && Array.isArray(data.items)) {
                    basket.items = data.items;
                }

                basket.values = {
                    ...basket.values,
                    ...data.totals,
                };
                basket.hasUnavailableItem = false;
                basket.hasTotalsError = false;
                basket.lastTotalsErrorMessage = undefined;

                if (data.discountApplied === 'product' || data.discountApplied === 'reward') {
                    delete basket.voucher;
                    basket.voucherCodeEntered = '';
                }
                if (data.discountApplied === 'voucher') {
                    basket.items = basket.items.map(item => {
                        item.pricing.discountToUse = 'none';
                        item.pricing = CalculateDiscountPrice(item.pricing, item.qty);
                        return item;
                    });
                }
                this.saveBasketChanges(basket, 'calculateBasketTotal');
                res(basket);
            }, (error) => {
                this.handleOrderTotalsError(basket, error);
                res(basket);
            });
        });
    }

    public recalculateBasketDelivery(basket: Basket): Basket {
        if (!this.basketRequiresDelivery(basket)) {
            return this.markBasketForCollection(basket);
        }

        if (basket.customDelivery.active) {
            basket.values.delivery = basket.customDelivery.value;
            basket.values.deliveryVat = +((+basket.customDelivery.value / 100) * 20).toFixed(2);
        } else {
            basket.values.delivery = 0;
            basket.values.deliveryVat = 0;
            for (const shipment of basket.shipments) {
                for (const servicesAvailable of shipment.courier.servicesAvailable) {
                    if (servicesAvailable.selected) {
                        if (basket.sampleOnly) {
                            basket.values.delivery = 0;
                            basket.values.deliveryVat = 0;
                        } else {
                            basket.values.delivery = basket.values.delivery + +servicesAvailable.cost;
                            basket.values.deliveryVat = basket.values.deliveryVat + +servicesAvailable.vat;
                        }
                    }
                }
            }
        }

        return basket;
    }

    calculateDeliveryCosts(basket: Basket) {
        if (!this.basketRequiresDelivery(basket)) {
            return of({
                errors: [],
                shipments: [],
                qualifiedForFreeDelivery: false,
            });
        }
        if (!basket.items.length && !basket.samples.length) {
            return of({
                errors: [],
                shipments: [],
                qualifiedForFreeDelivery: false,
            });
        }

        const dataToSend = {
            basket,
        };

        return this.http.post(env.apiPath + 'courier/delivery/costs', dataToSend).pipe(map(({ data }: any) => data));
    }

    reCalculateOrder(order: {
        items: any[];
        delivery: { amount: number; vat: number };
        voucher: any;
        rewardsDiscountPercentage: any;
        discount: { discountType: string; discountValue: string } | null;
    }): Observable<any> {
        return this.http.post(env.apiPath + 'orders/totals', order);
    }

    deleteBasketItem(idx) {
        const basket: Basket = this.store.selectForLocal<Basket>('basket');
        basket.items.splice(idx, 1);
        basket.sampleOnly = this.checkSampleOnly(basket);
        this.store.set('basket', basket);
        this.calculateBasketTotal(basket, this.shouldRecalculateDeliveryAfterBasketMutation(basket));
    }

    deleteBasketSample(idx) {
        const basket: Basket = this.store.selectForLocal<Basket>('basket');
        basket.samples.splice(idx, 1);
        basket.sampleOnly = this.checkSampleOnly(basket);
        this.store.set('basket', basket);
        this.calculateBasketTotal(basket, this.shouldRecalculateDeliveryAfterBasketMutation(basket));
    }

    checkSampleOnly(basket: Basket) {
        let sampleOnly = true;
        basket.items.forEach((item) => {
            if (!item.sample) {
                sampleOnly = false;
            }
        });
        return sampleOnly;
    }

    emptyBasket(basket: Basket) {
        basket.items = [];
        basket.samples = [];
        basket.potentialQty = 0;
        basket.voucher = undefined;
        basket.voucherCodeEntered = '';
        basket.deliveryInstructions = '';
        basket.differentDeliveryAddress = false;
        basket.fulfilmentMethod = 'delivery';
        basket.noDeliveryRequired = false;
        basket.deliveryInstructions = '';
        basket.token = undefined;
        basket.customer = undefined;
        basket.deliveryAddress = undefined;
        basket.billingAddress = undefined;
        basket.createdBy = undefined;
        basket.sampleOnly = false;
        basket.paymentMethod = 'BACS';
        basket.xeroQuoteId = undefined;
        basket.discount = {
            type: 'pound',
            value: 0,
        };
        localStorage.removeItem('yssAdminBasketId');
        delete basket.intent;
        // this.store.set('basketId', '');
        this.store.set('basket', basket);
        this.calculateBasketTotal(basket);
    }

    clearUserFromBasket() {
        const basket: Basket = this.store.selectForLocal<Basket>('basket');
        basket.deliveryInstructions = '';
        basket.token = undefined;
        basket.deliveryAddress = undefined;
        basket.billingAddress = undefined;
        basket.customer = undefined;
        basket.createdBy = undefined;
        this.store.set('basket', basket);
        this.calculateBasketTotal(basket);
    }

    deleteIntent() {
        const basket: Basket = this.store.selectForLocal<Basket>('basket');
        delete basket.intent;
        this.store.set('basket', basket);
    }

    saveBasketToStorage(basket: Basket) {
        basket = this.normaliseBasketState(basket);

        const dataToSend: any = {
            basket,
        };

        const basketId = this.getBasketId();
        if (basketId) {
            dataToSend.basketId = basketId;
        }

        this.store.set('basket', basket);

        return this.http.post(env.apiPath + 'basket', dataToSend);
    }

    updateBasketQuoteStatus(basketId: string, quoteStatus: UserQuoteStatus) {
        return this.http.put(env.apiPath + 'basket/quote/status', {
            basketId,
            quoteStatus,
        });
    }

    getQuoteBasket(basketId: string): Observable<any> {
        return this.http.get(env.apiPath + 'basket/quote/' + basketId).pipe(map((data: any) => data.data));
    }

    applyVoucher(voucherCode) {
        return this.http.get(env.apiPath + 'voucher/code/' + voucherCode).pipe(map((data: any) => data));
    }

    removeVoucher() {
        const basket: Basket = this.store.selectForLocal<Basket>('basket');
        basket.voucher = undefined;
        basket.voucherCodeEntered = '';
        this.store.set('basket', basket);
        this.calculateBasketTotal(basket);
    }

    public prepareBasketForSubmission(basket: Basket): Observable<Basket> {
        basket = this.normaliseBasketState(basket);
        const recalculateDelivery = this.shouldRecalculateDeliveryAfterBasketMutation(basket);

        return from(this.calculateBasketTotal(basket, recalculateDelivery)).pipe(
            switchMap((calculatedBasket: Basket) => {
                if (this.basketHasUnavailablePricing(calculatedBasket) || calculatedBasket.hasTotalsError) {
                    const message = calculatedBasket.lastTotalsErrorMessage ||
                        'Cannot create the order until basket totals have recalculated successfully.';

                    return throwError(() => new Error(message));
                }

                return of(this.normaliseBasketState(calculatedBasket));
            })
        );
    }

    processBasket(basket: Basket) {
        return this.prepareBasketForSubmission(basket).pipe(
            switchMap((calculatedBasket: Basket) => {
                calculatedBasket.createdBy = this.store.selectForLocal<UserData>('user');
                calculatedBasket.source = 'admin';

                const dataToSend = {
                    basket: calculatedBasket,
                    basketId: this.getBasketId(),
                };

                return this.http.post(env.apiPath + 'orders/create', dataToSend).pipe(map((data: any) => data));
            })
        );
    }

    emailOrderToCustomer(id): Observable<any> {
        return this.http.patch(env.apiPath + `basket/quote/${id}/email`, {}).pipe(map(({ data }: any) => data));
    }

    addBespokeItems(lists) {
        let basket: Basket = this.store.selectForLocal<Basket>('basket');

        for (let l = 0; l < lists.length; l++) {
            for (let i = 0; i < lists[l].items.length; i++) {
                const sku: any = {
                    skuId: '',
                    productDetailId: 0,
                    product: { id: '', productCode: '', name: '', slug: '', image: '' },
                    qty: 10,
                    unitPrice: 0,
                    totalPrice: 0,
                    weight: 0,
                    leadtime: 0,
                };

                sku.product = lists[l].items[i];
                sku.product.isBespoke = true;
                sku.sample = false;
                sku.product.id = 0;
                sku.product.productCode = '';
                sku.product.name = '';
                sku.product.slug = '';
                sku.product.supplier = {
                    id: lists[l].items[i].range.supplier,
                };
                sku.product.rangeId = sku.product.range.id;

                sku.sellUnit = lists[l].items[i].sellUnit;
                sku.product.image = lists[l].items[i].range.imageUrl;
                sku.product.name = `Bespoke ${lists[l].items[i].range.name} ${sku.product.type.name}`;
                sku.product.productCode = 'bespoke';
                if (sku.product.type.sellUnits.length) {
                    sku.sellUnit = sku.product.type.sellUnits[0];
                }
                sku.skuId = '';
                sku.productDetailId = 0;
                sku.qty = +lists[l].items[i].quantity;
                sku.leadtime = +lists[l].items[i].leadtime;
                // pallet weight was being calculated incorrectly as it was multiplying by the QTY in the courier API method
                // sku.weight = +item.weightPerSellUnit * +item.quantity;
                sku.weight = +lists[l].items[i].weightPerSellUnit;
                sku.sellUnit = lists[l].items[i].sellUnit;
                sku.unitPrice = +lists[l].items[i].pricePerSellUnit;
                sku.totalPrice = +lists[l].items[i].unitCostBreakdown.totalCost;
                sku.unitVatPrice = +((+sku.unitPrice / 100) * 20).toFixed(2);
                sku.unitPriceGross = +(+sku.unitPrice + +sku.unitVatPrice).toFixed(2);
                sku.pricing = this.buildBespokeLinePricing(+sku.unitPrice, +sku.totalPrice, +sku.qty, false);
                sku.totals = sku.pricing.totals;
                sku.totalPrice = +sku.pricing.totals.net;
                sku.totalVat = +sku.pricing.totals.vat;
                sku.totalPriceGross = +sku.pricing.totals.gross;
                sku.totalDiscount = +sku.pricing.totals.discount;
                sku.totalPricePreDiscount = +sku.pricing.totals.preDiscount.net;
                sku.piecesRequired = +lists[l].items[i].unitCostBreakdown.piecesRequired;
                sku.singlePieceSize = +lists[l].items[i].unitCostBreakdown.singlePieceSize;
                sku.quantityMetres = +lists[l].items[i].unitCostBreakdown.squareMetres;

                if (lists[l].items[i].sellUnit.id === 'PLM') {
                    sku.quantityMetres = +lists[l].items[i].unitCostBreakdown.linearMetres;
                }

                delete sku.product.range;

                // this.addToBasket(sku);

                basket.items.push(sku);
                basket = this.checkForSampleOnly(basket);
            }
        }
        this.saveBasketChanges(basket, 'basket.service.ts');
        return this.calculateBasketTotal(basket, this.shouldRecalculateDeliveryAfterBasketMutation(basket));
    }

    checkForSampleOnly(basket) {
        basket.sampleOnly = true;
        basket.items.forEach((item) => {
            if (!item.sample) {
                basket.sampleOnly = false;
            }
        });

        return basket;
    }

    canBeConvertedToOrder(basket): boolean {
        return basket.type === 'quote' && basket.sampleOnly === true && basket.samples.length;
    }

    buildSkuPrice(productDetailLine, qty: number): SkuPrice {
        const pricing: SkuPrice = {
            totals: defaultSkuPriceTotals(),
            basePrice: +productDetailLine.preDiscountPrice,
            net: +productDetailLine.preDiscountPrice,
            vat: 0,
            gross: 0,
            preDiscount: {
                net: +productDetailLine.preDiscountPrice,
                vat: +((+productDetailLine.preDiscountPrice / 100) * 20),
                gross: +((+productDetailLine.preDiscountPrice / 100) * 20 + +productDetailLine.preDiscountPrice),
            },
            discount: this.buildDiscountValues(productDetailLine),
            discountToUse: 'none',
        };

        return CalculateDiscountPrice(pricing, qty, false);
    }

    buildDiscountValues(productDetailLine): DiscountValue {
        const discount: DiscountValue = {
            preDiscountValue: +productDetailLine.preDiscountPrice,
            reward: {
                percentage: 0,
                value: 0,
            },
            product: {
                percentage: +productDetailLine.discount,
                value: 0,
            },
        };

        discount.product.value = (+productDetailLine.preDiscountPrice / 100) * +productDetailLine.discount;

        const user = this.store.selectForLocal('user');
        if (user && user.rewardDiscount) { // public user buy for him self
            discount.reward.percentage = user.rewardDiscount;
            discount.reward.value = (+productDetailLine.preDiscountPrice / 100) * user.rewardDiscount;
        } else { // the case on the admin
            const basket = this.store.selectForLocal('basket');
            if (basket.customer?.rewardDiscount > 0) {
                discount.reward.percentage = basket.customer.rewardDiscount;
                discount.reward.value = (+productDetailLine.preDiscountPrice / 100) * basket.customer.rewardDiscount;
            }
        }

        return discount;
    }
}
